import { useCallback, useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { TarjaDeOrcamento } from './TarjaDeOrcamento'
import { cn } from '../lib/cn'
import { TEXTOS } from '../lib/glossario'
import { grupoDoItem, itemAtivo } from '../lib/navegacao'
import { useTituloDaPagina } from '../lib/tituloDaPagina'
import { usePreferenciaLocal } from '../lib/preferencias'
import { versaoDoSistema } from '../lib/versao'
import { BarraLateral } from './layout/BarraLateral'
import { BarraSuperior } from './layout/BarraSuperior'

/**
 * A moldura da aplicação: menu lateral, faixa superior e a tela pedida no meio.
 *
 * O link "pular para o conteúdo" é o primeiro elemento focável da página. Sem ele, quem navega
 * por teclado percorre os itens do menu a cada troca de tela.
 *
 * A gaveta fecha ao navegar de dois jeitos, e os dois são necessários: o clique no item chama
 * `fechar` direto, e o efeito abaixo cobre o que não é clique — voltar pelo navegador, ou clicar
 * no item da tela em que já se está, quando a rota não muda mas a gaveta continuaria por cima.
 *
 * @returns a moldura com a rota atual dentro
 */
export function Layout() {
  const local = useLocation()
  useTituloDaPagina(local.pathname, local.search)
  const [gavetaAberta, setGavetaAberta] = useState(false)
  // Lembrado no navegador: quem recolhe o menu para ganhar espaço não quer reencontrá-lo aberto no próximo F5.
  const [recolhida, setRecolhida] = usePreferenciaLocal('menu-recolhido', false)
  const item = itemAtivo(local.pathname, local.search)

  const fecharGaveta = useCallback(() => setGavetaAberta(false), [])

  useEffect(() => {
    setGavetaAberta(false)
  }, [local.pathname, local.search])

  return (
    <div className="min-h-screen bg-fundo">
      <a
        href="#conteudo"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-white focus:px-3 focus:py-2 focus:shadow"
      >
        Pular para o conteúdo
      </a>

      <BarraLateral
        aberta={gavetaAberta}
        recolhida={recolhida}
        aoFechar={fecharGaveta}
        aoAlternarRecolhida={() => setRecolhida(!recolhida)}
      />

      <div
        className={cn(
          'flex min-h-screen flex-col transition-[padding] duration-200',
          recolhida ? 'lg:pl-[4.5rem]' : 'lg:pl-[17rem]',
        )}
      >
        <BarraSuperior
          titulo={item?.rotulo}
          grupo={item ? grupoDoItem(item)?.rotulo : undefined}
          resumo={item?.resumo}
          gavetaAberta={gavetaAberta}
          aoAlternarGaveta={() => setGavetaAberta((valor) => !valor)}
        />

        {/* `max-w-screen-2xl` (1536px), e não o `7xl`: num monitor de 1920px o `7xl` deixava quase
            500px vazios à direita enquanto a tabela de chamados — nove colunas a partir de `2xl` —
            transbordava da própria caixa e escondia "De onde veio" atrás da rolagem. O limite
            continua existindo para monitor ultralargo; texto corrido segue preso ao `max-w-prose`
            de cada tela. */}
        <main id="conteudo" className="mx-auto w-full max-w-screen-2xl flex-1 px-4 py-6 lg:px-8 lg:py-8">
          {/* A tarja do crédito de IA. Ela se decide sozinha: só aparece em /admin/* e só para quem
              administra — fora dali seria um valor de IA ao lado da fiscalização, que é a leitura
              que a V31 recusou. Tarja e não toast: toast some e o problema não. */}
          <TarjaDeOrcamento />
          <Outlet />
        </main>

        {/* Identificação institucional e versão, em todas as telas, numa linha. A versão vem da TAG
            da imagem publicada, injetada em tempo de build (ver lib/versao.ts) — escrita à mão, um
            rodapé de versão passa a mentir no primeiro release em que ninguém lembra de trocá-la.
            A definição de "Solicitação de Mudança" que morava aqui saiu: ela aparecia também nas
            telas de chamados e de crédito de IA, que não têm nada a ver com mudança. */}
        <footer className="mx-auto flex w-full max-w-screen-2xl flex-wrap justify-between gap-x-6 gap-y-1 border-t border-slate-200 px-4 py-5 text-xs text-slate-500 lg:px-8">
          <p>{TEXTOS.orgao}</p>
          <p>Versão {versaoDoSistema()}</p>
        </footer>
      </div>
    </div>
  )
}
